import "../../pages/new/new.scss"

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { getCodeExaminers } from '../../services/instructorService';
import { getThesisStatusById, updateCodeExaminer } from '../../services/thesisAssignedService';
import { toast } from 'react-toastify';
import TextBox from './TextBox';

export const AssignCodeExaminer = () => {
    const { thesisStatusId } = useParams();
    const navigate = useNavigate();

    const [submission, setSubmission] = useState({
        date_submitted: '',
        enrollment_number: '',
        id: '',
        mentor1_name: '',
        mentor2_name: '',
        report_type: '',
        report_month: '',
        student_name: '',
        thesis_title: '',
        folder_link: '',
        report_link: '',
        presentation_link: '',
        presentation_video_link: '',
        turnitin: '',
        rebuttal_report_link: '',
        finalFeedback: '',
        share_to_student: false,
        nextActionPoints: '',
        shareActionPoints: false,
        resubmissionDeadline: '',
        shareResubmissionDeadline: false,
        codeExaminerId: null,
    });

    const [examiners, setExaminers] = useState([]);
    const [selectedCodeExaminerId, setSelectedCodeExaminerId] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [fileLinks, setFileLinks] = useState({});

    const reportUploaded = uploadedFiles?.Report;
    const presentationUploaded = uploadedFiles?.Presentation_File;
    const presentationVideoUploaded = uploadedFiles?.Presentation_Video;
    const turnitinUploaded = uploadedFiles?.Turnitin;
    const rebuttalUploaded = uploadedFiles?.Rebuttal_Report_File;
    const codeDemoUploaded = uploadedFiles?.Code_Demo_File;

    const isDriveLink = (value) =>
        typeof value === "string" && value.startsWith("http");

    const fetchSubmission = async () => {
        try {
            const data = await getThesisStatusById(thesisStatusId);
            setSubmission(data.data);
            setSelectedCodeExaminerId(data.data.codeExaminerId);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchExaminerList = async () => {
        try {
            const response = await getCodeExaminers();
            setExaminers(response.data);
        } catch (error) {
            console.error('Error fetching examiners:', error);
        }
    };

    useEffect(() => {
        fetchSubmission();
        fetchExaminerList();
    }, []);

    useEffect(() => {
        if (!thesisStatusId) return;
        axiosInstance
            .get(`/files/status/${thesisStatusId}`)
            .then(res => setUploadedFiles(res.data.data))
            .catch(console.error);
    }, [thesisStatusId]);

    const fetchFileLink = async (fileType) => {
        try {
            const res = await axiosInstance.get("/files/presign-download", {
                params: { thesisStatusId, fileType }
            });
            setFileLinks(prev => ({
                ...prev,
                [fileType]: {
                    url: res.data.data.downloadUrl,
                    originalFilename: res.data.data.originalFilename
                }
            }));
        } catch (e) {
            console.error(`Failed to fetch ${fileType}`, e);
        }
    };

    useEffect(() => {
        if (!thesisStatusId) return;
        if (reportUploaded) fetchFileLink("Report");
        if (presentationUploaded) fetchFileLink("Presentation_File");
        if (presentationVideoUploaded) fetchFileLink("Presentation_Video");
        if (turnitinUploaded) fetchFileLink("Turnitin");
        if (rebuttalUploaded) fetchFileLink("Rebuttal_Report_File");
        if (codeDemoUploaded) fetchFileLink("Code_Demo_File");
    }, [
        thesisStatusId,
        reportUploaded,
        presentationUploaded,
        presentationVideoUploaded,
        turnitinUploaded,
        rebuttalUploaded,
        codeDemoUploaded
    ]);

    const handleCodeExaminerSelect = (event) => {
        setSelectedCodeExaminerId(event.target.value);
    };

    const goBack = () => {
        navigate('/assigncodeexaminers');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedCodeExaminerId) {
            toast.error("Please select a code examiner");
            return;
        }
        try {
            await updateCodeExaminer(thesisStatusId, selectedCodeExaminerId);
            toast.success("Code examiner assigned successfully");
            navigate('/assigncodeexaminers');
        } catch (error) {
            console.error(error);
            toast.error("Failed to assign code examiner");
        }
    };

    return (
        <div>
            <div className="top">
                <h1>Assign Code Examiner</h1>
            </div>
            <div className="bottom">
                <div className="left"></div>
                <div className="right">
                    <form onSubmit={handleSubmit}>
                        <div className="formInput">
                            <label>Student Name</label>
                            <input type="text" value={submission.student_name} disabled />
                        </div>
                        <div className="formInput">
                            <label>Enrollment Number</label>
                            <input type="text" value={submission.enrollment_number} disabled />
                        </div>
                        <div className="formInput">
                            <label>Mentor1</label>
                            <input type="text" value={submission.mentor1_name} disabled />
                        </div>
                        <div className="formInput">
                            <label>Mentor2</label>
                            <input type="text" value={submission.mentor2_name} disabled />
                        </div>
                        <div className="formInput">
                            <label>Report Type</label>
                            <input type="text" value={submission.report_type} disabled />
                        </div>
                        <div className="formInput">
                            <label>Thesis Title</label>
                            <TextBox value={submission.thesis_title} readOnly />
                        </div>

                        <div className="formInput">
                            <label>Folder Link</label>
                            {submission.folder_link ? (
                                <a href={submission.folder_link} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not available</div>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Report File</label>
                            {submission.report_link && isDriveLink(submission.report_link) ? (
                                <a href={submission.report_link} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : reportUploaded && fileLinks.Report?.url ? (
                                <a href={fileLinks.Report.url} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not uploaded</div>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Presentation File</label>
                            {submission.presentation_link && isDriveLink(submission.presentation_link) ? (
                                <a href={submission.presentation_link} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : presentationUploaded && fileLinks.Presentation_File?.url ? (
                                <a href={fileLinks.Presentation_File.url} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not uploaded</div>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Presentation Video</label>
                            {submission.presentation_video_link && isDriveLink(submission.presentation_video_link) ? (
                                <a href={submission.presentation_video_link} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : presentationVideoUploaded && fileLinks.Presentation_Video?.url ? (
                                <a href={fileLinks.Presentation_Video.url} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not uploaded</div>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Turnitin</label>
                            {submission.turnitin && isDriveLink(submission.turnitin) ? (
                                <a href={submission.turnitin} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : turnitinUploaded && fileLinks.Turnitin?.url ? (
                                <a href={fileLinks.Turnitin.url} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not uploaded</div>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Rebuttal Report File</label>
                            {submission.rebuttal_report_link && isDriveLink(submission.rebuttal_report_link) ? (
                                <a href={submission.rebuttal_report_link} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : rebuttalUploaded && fileLinks.Rebuttal_Report_File?.url ? (
                                <a href={fileLinks.Rebuttal_Report_File.url} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not uploaded</div>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Code Demo File</label>
                            {codeDemoUploaded && fileLinks.Code_Demo_File?.url ? (
                                <a href={fileLinks.Code_Demo_File.url} target="_blank" rel="noopener noreferrer">
                                    Visit Link
                                </a>
                            ) : (
                                <div style={{ color: "#999" }}>Not uploaded</div>
                            )}
                        </div>

                        <div
                            className="formInput"
                            style={{
                                display: "block",
                                background: "#f8f8f8",
                                border: "1px solid #ddd",
                                borderRadius: 6,
                                padding: "10px 12px",
                            }}
                        >
                            <label style={{ fontWeight: 600 }}>Current Feedback (shared with student)</label>
                            {submission.share_to_student && submission.finalFeedback ? (
                                <p style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{submission.finalFeedback}</p>
                            ) : (
                                <p style={{ color: "#999", marginTop: 8 }}>No feedback has been shared with the student yet.</p>
                            )}
                            {submission.shareActionPoints && submission.nextActionPoints && (
                                <>
                                    <label style={{ fontWeight: 600 }}>Next Action Points (shared)</label>
                                    <p style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{submission.nextActionPoints}</p>
                                </>
                            )}
                            {submission.shareResubmissionDeadline && submission.resubmissionDeadline && (
                                <>
                                    <label style={{ fontWeight: 600 }}>Resubmission Deadline (shared)</label>
                                    <p style={{ marginTop: 4 }}>{submission.resubmissionDeadline}</p>
                                </>
                            )}
                        </div>

                        <div className="formInput">
                            <label>Select Code Examiner</label>
                            <select
                                id="styled-dropdown"
                                value={selectedCodeExaminerId || ""}
                                onChange={handleCodeExaminerSelect}
                            >
                                <option value="">Select a code examiner</option>
                                {examiners && examiners.map(option => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}, {option.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="buttons">
                            <button type="submit">Save</button>
                            <button type="button" onClick={goBack}>Back</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AssignCodeExaminer;
